import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Menu,
  X,
  Home,
  Route,
  Trophy,
  User,
  Search,
  Shield,
  FileText,
  BarChart3,
  Settings,
  Brain,
  Smartphone,
  Building2,
  Palette,
  Users,
  Activity,
  Lock,
  Database,
  Zap,
  Network,
  Heart,
  Users2,
  Briefcase,
  ChevronDown,
  ChevronRight
} from "lucide-react";
import SimpleNotificationCenter from "@/components/notifications/SimpleNotificationCenter";

const mainItems = [
  { name: "🚁 Red Helicopter Journey", href: "/helicopter-journey", icon: Zap, description: "Immersive 3-stage transformation: Connect → Engage → Let's Go" },
  { name: "Interactive 3D Helicopter", href: "/helicopter", icon: Heart, description: "3D helicopter building experience with interactive visual states" },
  { name: "My Achievements", href: "/achievements", icon: Trophy, description: "Personal progress & milestones" },
  { name: "AI Learning Insights", href: "/ai-insights", icon: Brain, description: "Work-focused learning analytics & recommendations" },
];

const preWorkItems = [
  { name: "Agency Assessment", href: "/agency-assessment", icon: BarChart3, description: "Personal agency evaluation" },
  { name: "Tech Readiness", href: "/tech-readiness", icon: Settings, description: "Personal technology comfort assessment" },
  { name: "Skills Assessment", href: "/tech-baseline", icon: Zap, description: "Digital skills and workplace capabilities evaluation" },
  { name: "Research & Analytics", href: "/research", icon: Search, description: "Community insights & data analysis" },
  { name: "Evidence-based Onboarding", href: "/onboarding", icon: Route, description: "Role-specific pathways with competency assessments", disabled: true },
];

const administrativeItems = [
  { name: "Enterprise Analytics", href: "/enterprise", icon: Network, description: "Values-centered business intelligence & balance sheet revolution" },
  { name: "MVP Status Dashboard", href: "/mvp-status", icon: BarChart3, description: "30-day sprint progress tracking & deliverables" },
  { name: "Security Dashboard", href: "/security", icon: Shield, description: "Enterprise security & compliance monitoring" },
  { name: "Document Center", href: "/documents", icon: FileText, description: "Executive overview documents & reports" },
  { name: "Data Admin", href: "/admin", icon: Database, description: "Tenant data management & multi-cloud configurations" },
  { name: "Org Management", href: "/organization", icon: Building2, description: "Administrative management & organizational oversight", disabled: true },
  { name: "System Health", href: "/health", icon: Activity, description: "Platform monitoring for the community" },
  { name: "Mobile Tools", href: "/mobile-pwa", icon: Smartphone, description: "Mobile productivity features", disabled: true },
];

const communityItems: { name: string; href: string; icon: any; description: string; }[] = [];

const coreItems: { name: string; href: string; icon: any; description: string; }[] = [];

const supportItems = [
  { name: "Help Center", href: "/help", icon: FileText, description: "Documentation and support resources" },
];

export default function MainNavigation() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [location] = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  // Collapsible section state
  const [isAdministrativeExpanded, setIsAdministrativeExpanded] = useState(true);
  const [isCommunityExpanded, setIsCommunityExpanded] = useState(true);
  const [isPreWorkExpanded, setIsPreWorkExpanded] = useState(true);
  const [isSupportExpanded, setIsSupportExpanded] = useState(false);

  // Update CSS variable for layout spacing
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-width', 
      isSidebarOpen ? '16rem' : '0rem'
    );
  }, [isSidebarOpen]);

  const isActive = (href: string) => location === href;

  // Helper component for collapsible sections
  const CollapsibleSection = ({ 
    title, 
    icon: Icon, 
    items, 
    isExpanded, 
    onToggle, 
    colorClass = "text-gray-700",
    iconColorClass = "text-gray-500"
  }: {
    title: string;
    icon: any;
    items: any[];
    isExpanded: boolean;
    onToggle: () => void;
    colorClass?: string;
    iconColorClass?: string;
  }) => (
    <div>
      <div 
        className="flex items-center px-3 mb-2 cursor-pointer hover:bg-gray-50 rounded-md py-3 min-h-[44px] transition-colors touch-manipulation"
        onClick={onToggle}
      >
        <Icon className={`h-4 w-4 mr-2 ${iconColorClass}`} />
        <h3 className={`text-xs font-semibold uppercase tracking-wider flex-1 ${colorClass}`}>
          {title}
        </h3>
        {isExpanded ? (
          <ChevronDown className="h-3 w-3 text-gray-400" />
        ) : (
          <ChevronRight className="h-3 w-3 text-gray-400" />
        )}
      </div>
      {isExpanded && (
        <div className="space-y-1">
          {items.map((item) => {
            const ItemIcon = item.icon;
            const isDisabled = item.disabled;
            
            if (isDisabled) {
              return (
                <div
                  key={item.name}
                  className="group flex items-center px-2 py-3 min-h-[44px] text-sm font-medium rounded-md opacity-50 cursor-not-allowed touch-manipulation"
                  title={`${item.description} (Coming Soon)`}
                >
                  <ItemIcon className="mr-3 h-5 w-5 flex-shrink-0 text-gray-300" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-400">{item.name}</div>
                    <div className="text-xs text-gray-300 mt-0.5">Coming Soon</div>
                  </div>
                </div>
              );
            }
            
            return (
              <Link key={item.name} href={item.href}>
                <div
                  className={cn(
                    "group flex items-center px-2 py-3 min-h-[44px] text-sm font-medium rounded-md transition-colors touch-manipulation",
                    isActive(item.href)
                      ? "bg-slate-100 text-slate-700 border-l-4 border-slate-500"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  )}
                  title={item.description}
                >
                  <ItemIcon
                    className={cn(
                      "mr-3 h-5 w-5 flex-shrink-0",
                      isActive(item.href) ? "text-slate-500" : "text-gray-400 group-hover:text-gray-500"
                    )}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{item.name}</div>
                    {isActive(item.href) && (
                      <div className="text-xs text-slate-600 mt-0.5">{item.description}</div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Top header bar with notifications */}
      <div className="fixed top-0 right-0 left-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center space-x-4">
            {/* Menu button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="shadow-md"
            >
              {isSidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </Button>
            
            {/* Home Button */}
            <Link href="/">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center space-x-2 shadow-md hover:bg-gray-50 transition-colors"
              >
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">Home</span>
              </Button>
            </Link>
            
            {/* Red Helicopter Logo */}
            <Link href="/">
              <div className="flex items-center hover:opacity-80 transition-opacity cursor-pointer">
                <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Palette className="w-5 h-5 text-white" />
                </div>
                <div className="ml-2 hidden sm:block">
                  <h1 className="text-lg font-bold text-gray-900">Red Helicopter</h1>
                </div>
              </div>
            </Link>
          </div>
          
          {/* Notification Center */}
          <div className="flex items-center space-x-4">
            <SimpleNotificationCenter />
          </div>
        </div>
      </div>

      {/* Sidebar overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-64 transform transition-transform duration-300 ease-in-out",
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col bg-white border-r border-gray-200 shadow-lg">
          {/* Logo and title with helicopter construction metaphor */}
          <div className="flex h-16 flex-shrink-0 items-center px-4 border-b border-gray-200">
            <Link href="/">
              <div className="flex items-center hover:opacity-80 transition-opacity cursor-pointer">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Palette className="w-6 h-6 text-white" />
                </div>
                <div className="ml-3 overflow-hidden">
                  <h1 className="text-lg font-bold text-gray-900 whitespace-nowrap">Red Helicopter</h1>
                  <p className="text-xs text-gray-500 whitespace-nowrap">Leadership Journey</p>
                </div>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-6 overflow-y-auto">
            {/* Core Dashboard */}
            <div>
              <div className="mt-2 space-y-1">
                {coreItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.name} href={item.href}>
                      <div
                        className={cn(
                          "group flex items-center px-2 py-3 min-h-[44px] text-sm font-medium rounded-md transition-colors touch-manipulation relative",
                          isActive(item.href)
                            ? "bg-slate-100 text-slate-700 border-l-4 border-slate-500"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        )}
                        title={item.description}
                      >
                        <Icon
                          className={cn(
                            "h-5 w-5 flex-shrink-0 mr-3",
                            isActive(item.href) ? "text-slate-500" : "text-gray-400 group-hover:text-gray-500"
                          )}
                        />
                        <div className="flex-1 overflow-hidden">
                          <div className="text-sm font-medium whitespace-nowrap">{item.name}</div>
                          {isActive(item.href) && (
                            <div className="text-xs text-slate-600 mt-0.5 whitespace-nowrap">{item.description}</div>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Main Items - Promoted */}
            <div className="space-y-1 mb-4">
              {mainItems.map((item) => {
                const ItemIcon = item.icon;
                return (
                  <Link key={item.name} href={item.href}>
                    <div
                      className={cn(
                        "group flex items-center px-2 py-3 min-h-[44px] text-sm font-medium rounded-md transition-colors touch-manipulation",
                        isActive(item.href)
                          ? "bg-slate-100 text-slate-700 border-l-4 border-slate-500"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      )}
                      title={item.description}
                    >
                      <ItemIcon
                        className={cn(
                          "mr-3 h-5 w-5 flex-shrink-0",
                          isActive(item.href) ? "text-slate-500" : "text-gray-400 group-hover:text-gray-500"
                        )}
                      />
                      {item.name}
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Research Context */}
            <CollapsibleSection
              title="Research"
              icon={Search}
              items={preWorkItems}
              isExpanded={isPreWorkExpanded}
              onToggle={() => setIsPreWorkExpanded(!isPreWorkExpanded)}
              colorClass="text-gray-700"
              iconColorClass="text-purple-500"
            />

            {/* Admin Context */}
            <CollapsibleSection
              title="Admin"
              icon={Briefcase}
              items={administrativeItems}
              isExpanded={isAdministrativeExpanded}
              onToggle={() => setIsAdministrativeExpanded(!isAdministrativeExpanded)}
              colorClass="text-gray-700"
              iconColorClass="text-blue-500"
            />

            {/* Community Context - Hide if empty */}
            {communityItems.length > 0 && (
              <CollapsibleSection
                title="Community"
                icon={Users2}
                items={communityItems}
                isExpanded={isCommunityExpanded}
                onToggle={() => setIsCommunityExpanded(!isCommunityExpanded)}
                colorClass="text-gray-700"
                iconColorClass="text-green-500"
              />
            )}

            {/* Support */}
            <div className="pt-4 border-t border-gray-200">
              <CollapsibleSection
                title="Support"
                icon={Lock}
                items={supportItems}
                isExpanded={isSupportExpanded}
                onToggle={() => setIsSupportExpanded(!isSupportExpanded)}
                colorClass="text-gray-500"
                iconColorClass="text-gray-400"
              />
            </div>
          </nav>

          {/* Platform Status */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-900">All Systems Operational</p>
                <p className="text-xs text-gray-500">Security: Active</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 flex z-40">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileMenuOpen(false)}
                className="ml-1 flex items-center justify-center h-11 w-11 min-h-[44px] min-w-[44px] rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white touch-manipulation"
              >
                <X className="h-6 w-6 text-white" />
              </Button>
            </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4">
                <Link href="/">
                  <div className="flex items-center space-x-3 hover:opacity-80 transition-opacity cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                      <Palette className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h1 className="text-lg font-bold text-gray-900">Red Helicopter</h1>
                    </div>
                  </div>
                </Link>
              </div>
              <nav className="mt-5 px-2 space-y-1">
                {/* Mobile navigation items - simplified */}
                {[...coreItems, ...mainItems, ...administrativeItems, ...communityItems, ...preWorkItems, ...supportItems].map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.name} href={item.href}>
                      <div
                        className={cn(
                          "group flex items-center px-2 py-4 min-h-[44px] text-base font-medium rounded-md touch-manipulation",
                          isActive(item.href)
                            ? "bg-orange-100 text-orange-700"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        )}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Icon
                          className={cn(
                            "mr-4 h-6 w-6 flex-shrink-0",
                            isActive(item.href) ? "text-orange-500" : "text-gray-400 group-hover:text-gray-500"
                          )}
                        />
                        {item.name}
                      </div>
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      )}
    </>
  );
}